import { FC, useState } from 'react';

import { Button, Modal } from 'antd';
import { addReviewSchema } from 'common/validation';
import { Form, FormInput, FormSelect, FormTextArea, FormUploader } from 'components/Form';
import { DndImagesList } from 'components/DndImagesList';
import { notify } from 'common/utils';
import { createReview } from 'http/reviewApi';
import { TPhoto } from 'common/types';
import { useFormContext } from 'react-hook-form';

import styles from './add-review-modal.module.scss';

const RATING_OPTIONS = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map((v) => ({
  label: String(v),
  value: v,
}));

interface IAddReviewModalProps {
  productId: number;
  productTitle: string;
  isOpen: boolean;
  closeModal: () => void;
}

interface IAddReviewForm {
  reviewerName: string;
  rating: number;
  reviewText?: string | null;
  photos?: TPhoto[] | null;
}

const PhotosField: FC = () => {
  const { watch } = useFormContext();
  const photos = watch('photos');

  return (
    <>
      <FormUploader multiple name="photos" label="Photos" limit={5} />
      {!!photos?.length && (
        <DndImagesList photos={photos} name="photos" className={styles.imagesList} />
      )}
    </>
  );
};

export const AddReviewModal: FC<IAddReviewModalProps> = ({
  productId,
  productTitle,
  isOpen,
  closeModal,
}) => {
  const [loading, setLoading] = useState(false);

  const onSubmit = async (values: IAddReviewForm) => {
    setLoading(true);

    try {
      await createReview({
        productId,
        reviewerName: values.reviewerName,
        rating: values.rating,
        reviewText: values.reviewText,
        photos: values.photos ?? undefined,
      });

      notify('Review added successfully!');
      closeModal();
    } catch {
      notify('Failed to add review', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      className={styles.modalWrapper}
      destroyOnClose
      open={isOpen}
      footer={null}
      onCancel={closeModal}
    >
      <span className={styles.title}>Add review</span>
      {!!productTitle && <span className={styles.productTitle}>{productTitle}</span>}
      <div className={styles.formContainer}>
        <Form
          initialValues={{ reviewerName: '', rating: undefined, reviewText: '', photos: [] }}
          validation={addReviewSchema}
          onSubmit={onSubmit}
        >
          <div className={styles.fields}>
            <FormInput required name="reviewerName" label="Author" className={styles.field} />
            <FormSelect
              required
              name="rating"
              label="Rating"
              options={RATING_OPTIONS}
              placeholder="Select rating"
              className={styles.field}
              zIndex={1100}
            />
            <FormTextArea name="reviewText" label="Review" className={styles.field} rows={3} />
            <PhotosField />
            <div className={styles.buttonWrapper}>
              <Button
                loading={loading}
                disabled={loading}
                type="primary"
                htmlType="submit"
                className={styles.button}
              >
                Save
              </Button>
              <Button
                disabled={loading}
                onClick={closeModal}
                type="default"
                className={styles.button}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </Modal>
  );
};
